import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import type { AdminFishingEventRow, FishingEventStatusFilter, FishingEventsStats } from '../types';

export function useFishingEventsStats() {
    return useQuery<FishingEventsStats>({
        queryKey: ['admin', 'fishing-events', 'stats'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_fishing_events_stats');
            if (error) throw new Error(error.message);
            const row = normalizeRpcResponse(data)[0] as Record<string, unknown>;
            return {
                total: Number(row?.total ?? 0),
                pending_count: Number(row?.pending_count ?? 0),
                approved_count: Number(row?.approved_count ?? 0),
                rejected_count: Number(row?.rejected_count ?? 0),
            };
        },
        staleTime: 15_000,
    });
}

export function useAdminFishingEventsList(statusFilter: FishingEventStatusFilter, search: string) {
    return useQuery<AdminFishingEventRow[]>({
        queryKey: ['admin', 'fishing-events', 'list', statusFilter, search],
        queryFn: async () => {
            const params: Record<string, unknown> = { p_limit: 300, p_offset: 0 };
            if (statusFilter !== 'all') params.p_status_filter = statusFilter;
            if (search.trim()) params.p_search = search.trim();
            const { data, error } = await dbClient.rpc('admin_fishing_events_list', params);
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data) as AdminFishingEventRow[];
        },
        staleTime: 15_000,
    });
}

export function useAdminFishingEventSetStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: { id: string; status: string; rejectionReason?: string }) => {
            const { error } = await dbClient.rpc('admin_fishing_event_set_status', {
                p_id: args.id,
                p_new_status: args.status,
                p_rejection_reason: args.rejectionReason ?? null,
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-events'] });
        },
    });
}

export function useAdminFishingEventDelete() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const { error } = await dbClient.rpc('admin_fishing_event_delete', { p_id: id });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-events'] });
        },
    });
}

export function useAdminFishingEventUpdate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: {
            p_id: string;
            p_title: string;
            p_description: string | null;
            p_rules_considerations: string | null;
            p_event_type: string;
            p_starts_at: string;
            p_ends_at: string | null;
            p_location_label: string | null;
            p_country_code: string | null;
            p_latitude: number | null;
            p_longitude: number | null;
            p_external_url: string | null;
            p_registration_url: string | null;
            p_registration_fee_eur: number | null;
            p_organizer_contact: string | null;
            p_cover_image_url: string | null;
            p_max_participants: number | null;
            p_salida_community_intent: string | null;
            p_status: string | null;
        }) => {
            const { error } = await dbClient.rpc('admin_fishing_event_update', payload);
            if (error) throw new Error(error.message);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-events'] });
        },
    });
}

export function useAdminFishingEventCreate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: {
            p_title: string;
            p_description: string | null;
            p_rules_considerations: string | null;
            p_event_type: string;
            p_starts_at: string;
            p_ends_at: string | null;
            p_location_label: string | null;
            p_country_code: string | null;
            p_latitude: number | null;
            p_longitude: number | null;
            p_external_url: string | null;
            p_registration_url: string | null;
            p_registration_fee_eur: number | null;
            p_organizer_contact: string | null;
            p_cover_image_url: string | null;
            p_max_participants: number | null;
            p_salida_community_intent: string | null;
        }) => {
            const { data, error } = await dbClient.rpc('admin_fishing_event_create', payload);
            if (error) throw new Error(error.message);
            return data as string;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-events'] });
        },
    });
}
